(function () {
  const SECTION_HEADINGS = [
    'Other Dimensions',
    'Details',
    'Features',
    'Electrical',
    'Bulb Information',
    'Certifications',
    'Assembly',
    'Shipping',
    'Warranty',
    'Product Care',
  ];

  const LIKELY_KEYS = [
    'Canopy',
    'Fixture',
    'Blade Span',
    'Shade',
    'Back Plate',
    'Overall Dimensions',
    'Overall Product Weight',
    'Overall Maximum Height',
    'Overall Minimum Height',
    'Suspension Length',
    'Downrod Size(s)',
    'Pull Chain Length - End to End',
    'Body Width - Side to Side',
    'Body Depth - Front to Back',
    'Body Height - Top to Bottom',
    'Blade Width - Side to Side',
    'Blade Length - End to End',
    'Canopy Width - Side to Side',
    'Canopy Depth - Front to Back',
    'Canopy Height - Top to Bottom',
    'Shade Width - Side to Side',
    'Shade Depth - Front to Back',
    'Shade Height - Top to Bottom',
    'Width - Side to Side',
    'Depth - Front to Back',
    'Height - Top to Bottom',
    'Fixture Design',
    'Canopy Included',
    'Light Fixture Shape',
    'Number of Lights',
    'Adjustable Length',
    'Number of Light Tiers',
    'Light Direction',
    'Shade Included',
    'Downrod Included',
    'Fixture Material',
    'Sloped Ceiling Compatible',
    'Integrated LED',
    'Bulb Not Included But Required',
    'Bulb Base',
    'Dimmable',
    'Voltage',
    'Maximum Wattage (Per Bulb)',
    'Power Source',
    'Metal Type',
    'Craftsmanship Type',
    'Finish',
    'Dry, Damp or Wet Location Rated',
    'Adjustable Color Temperature',
    'Shade Characteristics',
    'Pieces Included',
    'Recommended Bulb Shape',
    'Recommended Bulb Shape Code',
    'Shade Material',
    'Shade Color',
    'Safety Listing(s)',
    'Assembly Required',
    'Mounting / Installation Required',
    'Commercial Warranty',
    'Commercial Warranty Length',
    'Product Warranty',
    'Warranty Length',
    'Full or Limited Warranty',
    'Warranty Details',
  ];

  const PRIORITY_FIELDS = [
    {
      label: 'Canopy',
      matchers: ['canopy'],
    },
    {
      label: 'Fixture',
      matchers: ['fixture'],
    },
    {
      label: 'Downrod Size(s)',
      matchers: ['downrod'],
    },
    {
      label: 'Overall Maximum Height',
      matchers: ['overall maximum height', 'maximum height'],
    },
    {
      label: 'Overall Product Weight',
      matchers: ['overall product weight', 'product weight', 'weight'],
    },
  ];

  const SECONDARY_FIELDS = [
    {
      label: 'Fixture Material',
      matchers: ['fixture material'],
      exact: true,
    },
    {
      label: 'Back Plate',
      matchers: ['back plate'],
      exact: true,
    },
    {
      label: 'Shade',
      matchers: ['shade'],
      exact: true,
    },
    {
      label: 'Overall Dimensions',
      matchers: ['overall dimensions'],
      exact: true,
    },
  ];

  const ROW_KEYS = [...new Set([...LIKELY_KEYS, ...PRIORITY_FIELDS.map((field) => field.label)])]
    .sort((left, right) => right.length - left.length);

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const buildFlexibleLabelPattern = (label) =>
    label
      .replace(/[–—]/g, '-')
      .trim()
      .split(/\s+/)
      .map((token) => {
        if (token === '-') return '[-–—]';
        return escapeRegExp(token);
      })
      .join('\\s+')
      .replace(/\\s\+\[-–—\]\\s\+/g, '\\s*[-–—]\\s*');

  const normalizeSpecificationsText = (value) =>
    String(value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\r/g, '\n')
      .replace(/[–—]/g, ' - ')
      .replace(/\|/g, ' : ')
      .replace(/\s*:\s*/g, ' : ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

  const addSectionBreaks = (value) => {
    let output = value
      .replace(/^Specifications\s*/i, '')
      .replace(/^Product Parameters\s*/i, '')
      .trim();

    SECTION_HEADINGS.forEach((heading) => {
      const pattern = new RegExp(`\\s*${escapeRegExp(heading)}\\s*`, 'gi');
      output = output.replace(pattern, `\n\n${heading}\n`);
    });

    return output.replace(/\n{3,}/g, '\n\n').trim();
  };

  const cleanLabel = (value) =>
    String(value || '')
      .replace(/^(?:H|W|D|L)\s+/i, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+([,.;:])/g, '$1')
      .trim();

  const cleanRowValue = (value) =>
    String(value || '')
      .replace(/^(?:\s*:\s*)+/, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+([,.;:])/g, '$1')
      .trim();

  const collectKnownMatches = (compactValue) => {
    const candidates = [];

    ROW_KEYS.forEach((label) => {
      const pattern = new RegExp(
        `(^|\\s)((?:H|W|D|L)\\s+)?(${buildFlexibleLabelPattern(label)})(?=(?:\\s*:+)?(?:\\s|$|["'0-9A-Za-z(]))`,
        'gi'
      );

      let match = pattern.exec(compactValue);

      while (match) {
        const rawPrefix = match[2] || '';
        const labelIndex = match.index + match[1].length + rawPrefix.length;
        const valueStart = match.index + match[0].length;

        candidates.push({
          label,
          index: labelIndex,
          valueStart,
          priority: 2,
          span: valueStart - labelIndex,
        });

        match = pattern.exec(compactValue);
      }
    });

    return candidates;
  };

  const collectColonMatches = (compactValue) => {
    const candidates = [];
    const keyPattern = /(^|\s)([A-Z][A-Za-z0-9()/'&+.,\-–\s]{1,90}?)(?=\s*:)/g;
    const seenAtIndex = new Set();
    let match = keyPattern.exec(compactValue);

    while (match) {
      const rawLabel = match[2];
      const leadingUnitPrefixLength = rawLabel.match(/^(?:H|W|D|L)\s+/i)?.[0]?.length || 0;
      const label = cleanLabel(rawLabel);
      const adjustedIndex = match.index + match[1].length + leadingUnitPrefixLength;

      if (
        label &&
        !SECTION_HEADINGS.includes(label) &&
        /[A-Za-z]/.test(label) &&
        !seenAtIndex.has(adjustedIndex)
      ) {
        seenAtIndex.add(adjustedIndex);
        candidates.push({
          label,
          index: adjustedIndex,
          valueStart: keyPattern.lastIndex,
          priority: 1,
          span: keyPattern.lastIndex - adjustedIndex,
        });
      }

      match = keyPattern.exec(compactValue);
    }

    return candidates;
  };

  const selectMatches = (matches) => {
    const sorted = [...matches].sort((left, right) => {
      if (left.index !== right.index) return left.index - right.index;
      if (left.priority !== right.priority) return right.priority - left.priority;
      return right.span - left.span;
    });

    const selected = [];

    sorted.forEach((match) => {
      const last = selected[selected.length - 1];

      if (!last) {
        selected.push(match);
        return;
      }

      if (match.index >= last.valueStart) {
        selected.push(match);
        return;
      }

      const currentScore = match.priority * 1000 + match.span;
      const lastScore = last.priority * 1000 + last.span;

      if (currentScore > lastScore) {
        selected[selected.length - 1] = match;
      }
    });

    return selected.sort((left, right) => left.index - right.index);
  };

  const extractRows = (value) => {
    const compactValue = normalizeSpecificationsText(value).replace(/\n+/g, ' ').trim();
    if (!compactValue) return [];

    const rows = [];
    const matches = selectMatches([...collectKnownMatches(compactValue), ...collectColonMatches(compactValue)]);

    matches.forEach((currentMatch, index) => {
      const nextMatch = matches[index + 1];
      const rawValue = compactValue.slice(currentMatch.valueStart, nextMatch ? nextMatch.index : compactValue.length);
      const value = cleanRowValue(rawValue);

      if (!value || SECTION_HEADINGS.includes(currentMatch.label)) return;

      rows.push({
        label: currentMatch.label,
        value,
      });
    });

    return rows;
  };

  const parseSections = (value) => {
    const normalized = addSectionBreaks(normalizeSpecificationsText(value));
    const chunks = normalized
      .split(/\n{2,}/)
      .map((chunk) => chunk.trim())
      .filter(Boolean);

    if (!chunks.length) return [];

    return chunks.map((chunk) => {
      const matchingHeading = SECTION_HEADINGS.find((heading) =>
        chunk.toLowerCase().startsWith(heading.toLowerCase())
      );

      const title = matchingHeading || '';
      const body = title ? chunk.slice(title.length).trim() : chunk;
      const rows = extractRows(body);

      return {
        title,
        body,
        rows,
      };
    });
  };

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (typeof text === 'string' && text.length) element.textContent = text;
    return element;
  };

  const normalizeKey = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const rowMatchesField = (row, field, usedLabels) => {
    const normalizedLabel = normalizeKey(row.label);
    if (!normalizedLabel || usedLabels.has(normalizedLabel)) return false;

    return field.matchers.some((matcher) => {
      const normalizedMatcher = normalizeKey(matcher);

      if (field.exact) {
        return normalizedLabel === normalizedMatcher;
      }

      return (
        normalizedLabel === normalizedMatcher ||
        normalizedLabel.startsWith(`${normalizedMatcher} `) ||
        normalizedLabel.includes(normalizedMatcher)
      );
    });
  };

  const renderRows = (rows) => {
    const list = createElement('div', 'vc-product__specifications-group-list');

    rows.forEach((row) => {
      const item = createElement('div', 'vc-product__specifications-group-item');
      item.append(
        createElement('div', 'vc-product__specifications-group-key', row.label),
        createElement('div', 'vc-product__specifications-group-value', row.value)
      );
      list.appendChild(item);
    });

    return list;
  };

  const renderSimpleRows = (container, rows) => {
    const body = container.querySelector('[data-vc-specifications-body]');
    if (!body) return;

    body.innerHTML = '';

    const list = createElement('div', 'vc-product__specifications-list vc-product__specifications-list--simple');

    rows.forEach((row) => {
      const item = createElement('div', 'vc-product__specifications-item');
      item.append(
        createElement('span', 'vc-product__specifications-item-label', row.label),
        createElement('span', 'vc-product__specifications-item-value', row.value)
      );
      list.appendChild(item);
    });

    body.appendChild(list);
  };

  const renderSections = (container, sections) => {
    const body = container.querySelector('[data-vc-specifications-body]');
    if (!body) return;

    body.innerHTML = '';

    sections.forEach((section) => {
      if (!section.title && !section.body && !section.rows.length) return;

      const group = createElement('section', 'vc-product__specifications-group');

      if (section.title) {
        group.appendChild(createElement('h4', 'vc-product__specifications-group-title', section.title));
      }

      if (section.rows.length) {
        group.appendChild(renderRows(section.rows));
      } else if (section.body) {
        group.appendChild(
          createElement('div', 'vc-product__specifications-group-content', section.body)
        );
      }

      body.appendChild(group);
    });
  };

  const getPriorityRows = (sections) => {
    const allRows = sections.flatMap((section) => section.rows || []);
    if (!allRows.length) return [];

    const selectedRows = [];
    const usedLabels = new Set();

    PRIORITY_FIELDS.forEach((field) => {
      const match = allRows.find((row) => rowMatchesField(row, field, usedLabels));

      if (!match) return;

      usedLabels.add(normalizeKey(match.label));
      selectedRows.push(match);
    });

    if (selectedRows.length >= PRIORITY_FIELDS.length) {
      return selectedRows.slice(0, PRIORITY_FIELDS.length);
    }

    const preferredSupplementRows = sections.flatMap((section) => {
      const normalizedTitle = normalizeKey(section.title);
      if (!normalizedTitle || normalizedTitle === 'other dimensions' || normalizedTitle === 'dimensions') {
        return section.rows || [];
      }
      return [];
    });

    const supplementRows = preferredSupplementRows.length ? preferredSupplementRows : allRows;

    SECONDARY_FIELDS.forEach((field) => {
      if (selectedRows.length >= PRIORITY_FIELDS.length) return;

      const match = supplementRows.find((row) => rowMatchesField(row, field, usedLabels));
      if (!match) return;

      usedLabels.add(normalizeKey(match.label));
      selectedRows.push(match);
    });

    supplementRows.forEach((row) => {
      if (selectedRows.length >= PRIORITY_FIELDS.length) return;

      const normalizedLabel = normalizeKey(row.label);
      if (!normalizedLabel || usedLabels.has(normalizedLabel)) return;

      usedLabels.add(normalizedLabel);
      selectedRows.push(row);
    });

    return selectedRows.slice(0, PRIORITY_FIELDS.length);
  };

  document.querySelectorAll('[data-vc-specifications]').forEach((container) => {
    const rawScript = container.querySelector('[data-vc-specifications-raw]');
    if (!rawScript) return;

    let rawText = '';

    try {
      rawText = JSON.parse(rawScript.textContent || '""');
    } catch (error) {
      // Error handled silently in production - product specifications parse failed
      rawText = rawScript.textContent || '';
    }

    const sections = parseSections(rawText);
    if (!sections.length) return;

    const priorityRows = getPriorityRows(sections);

    if (priorityRows.length) {
      renderSimpleRows(container, priorityRows);
      return;
    }

    renderSections(container, sections);
  });
})();
